/**
 * 🔒 Send receipt (piece 32; T2371; RFC 3798): the reader's own answer to a received message's
 * `Disposition-Notification-To`, sent from the mailbox the message arrived in through the ordinary Send.
 * 🔨 An incoming request is never answered by the system; ignoring it is silent and normal.
 */
import { parseAddressList } from './headers';
import { rawHeaderValue } from './unsubscribe';
import { READ_RECEIPT_HEADER } from './compose';

/** The route the reading pane's Send receipt posts to. */
export const mailReceiptPath = (id: string): string => `/mail/messages/${id}/receipt`;

/** The subject a receipt carries before the original's. */
export const READ_RECEIPT_SUBJECT_PREFIX = 'Read:';

/** What a receipt says the reader did: displayed it, by their own hand (RFC 3798, section 3.2.6). */
export const READ_RECEIPT_DISPOSITION = 'manual-action/MDN-sent-manually; displayed';

/** The address a stored header block asks a receipt to go to, or null where it asks for none. */
export function receiptRequestOf(rawHeaders: string): string | null {
  const value = rawHeaderValue(rawHeaders, READ_RECEIPT_HEADER);
  if (!value) return null;
  return parseAddressList(value)[0]?.address ?? null;
}

/** 🔨 Offered on received mail outside the Quarantine and outside a departed person's mailbox, and nowhere else. */
export function receiptOffered(facts: { direction: 'Inbound' | 'Outbound'; folderKind: string; departed: boolean }): boolean {
  return facts.direction === 'Inbound' && facts.folderKind !== 'Quarantine' && facts.folderKind !== 'Drafts' && !facts.departed;
}

/** The receipt's subject: Read: and the original's, kept to one header line. */
export function receiptSubject(subject: string): string {
  return `${READ_RECEIPT_SUBJECT_PREFIX} ${subject}`.trim().slice(0, 998);
}

/**
 * The receipt's words: who it was sent to, when, that it was displayed, and the disposition fields a mail program
 * reads, written out because the outbound seam carries no report part (piece 32, Open issues, 2026-10-27).
 */
export function receiptText(facts: { subject: string; to: string; sentAt: string | null; messageIdHeader: string }): string {
  const when = facts.sentAt ? ` on ${facts.sentAt}` : '';
  return [
    `This is a receipt for the message you sent to ${facts.to}${when}, "${facts.subject}".`,
    'It confirms the message was displayed by its recipient. It does not confirm the message was read or understood.',
    '',
    `Final-Recipient: rfc822; ${facts.to}`,
    `Original-Message-ID: ${facts.messageIdHeader}`,
    `Disposition: ${READ_RECEIPT_DISPOSITION}`,
  ].join('\n');
}

/** What the reading pane draws from: where a receipt would go, and whether one already went. */
export interface MailReceiptView {
  address: string;
  sent: boolean;
}

/** The answer to Send receipt: Sent, or Held under the person's undo delay, and where it went. */
export interface MailReceiptAnswer {
  outcome: 'Sent' | 'Held' | 'Failed';
  address: string;
}
